import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, DataTable, Modal, FormField, TextInput, Drawer, StatusBadge, Toggle, EmptyState, PageHeader } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';

interface Section { title: string; body: string }
interface Script {
  id: number;
  name: string;
  description?: string;
  active: boolean;
  tree?: { sections?: Section[] } | null;
}

export function AdminScripts({ embedded = false }: { embedded?: boolean } = {}) {
  const [rows, setRows] = useState<Script[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [editor, setEditor] = useState<Script | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Script | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    void adminApi
      .scripts()
      .then((res) => setRows(res.data as Script[]))
      .catch(() => toast('שגיאה בטעינת תסריטים', 'danger'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function add() {
    if (!form.name) {
      toast('יש למלא שם', 'danger');
      return;
    }
    try {
      await adminApi.createScript({ ...form, tree: { sections: [] }, active: true });
      toast('נוסף', 'ok');
      setCreateOpen(false);
      setForm({ name: '', description: '' });
      load();
    } catch {
      toast('שגיאה ביצירת תסריט', 'danger');
    }
  }

  async function toggleActive(s: Script, next: boolean) {
    setRows((prev) => prev.map((r) => (r.id === s.id ? { ...r, active: next } : r)));
    try {
      await adminApi.updateScript(s.id, { active: next });
    } catch {
      setRows((prev) => prev.map((r) => (r.id === s.id ? { ...r, active: !next } : r)));
      toast('שגיאה בעדכון', 'danger');
    }
  }

  async function remove(s: Script) {
    try {
      await adminApi.deleteScript(s.id);
      toast('נמחק', 'ok');
      setDeleteTarget(null);
      setRows((prev) => prev.filter((r) => r.id !== s.id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  async function duplicate(s: Script) {
    try {
      await adminApi.duplicateScript(s.id);
      toast('שוכפל', 'ok');
      load();
    } catch {
      toast('שגיאה בשכפול', 'danger');
    }
  }

  const cols: DataTableColumn<Script>[] = [
    { key: 'name', he: 'שם' },
    { key: 'description', he: 'תיאור', render: (r) => r.description || '—' },
    { key: 'sections', he: 'שלבים', align: 'end', render: (r) => String(r.tree?.sections?.length ?? 0) },
    {
      key: 'active',
      he: 'פעיל',
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Toggle checked={r.active} onChange={(v) => toggleActive(r, v)} />
        </div>
      ),
    },
    {
      key: 'actions',
      he: 'פעולות',
      align: 'end',
      render: (r) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="btn ghost" onClick={() => setEditor(r)}>ערוך</button>
          <button type="button" className="btn ghost" onClick={() => duplicate(r)}>שכפל</button>
          <button type="button" className="btn danger" onClick={() => setDeleteTarget(r)}>מחק</button>
        </div>
      ),
    },
  ];

  return (
    <div className="stack" style={embedded ? undefined : { padding: 'var(--gap)' }}>
      {embedded ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>תסריטי שיחה שמנחים את הנציגים בזמן שיחה.</span>
          <button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>+ תסריט חדש</button>
        </div>
      ) : (
        <PageHeader title="תסריטים" actions={<><button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>+ תסריט חדש</button></>} />
      )}
      <Card padded={false}><DataTable<Script> columns={cols} rows={rows} loading={loading} onRowClick={setEditor} emptyState={<EmptyState icon="info" title="אין עדיין תסריטים" description="צור תסריט שיחה ראשון כדי להנחות את הנציגים." action={<button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>+ תסריט חדש</button>} />} /></Card>

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="תסריט חדש"
        footer={<><button type="button" className="btn" onClick={() => setCreateOpen(false)}>ביטול</button><button type="button" className="btn primary" onClick={add}>שמור</button></>}>
        <div className="stack">
          <FormField label="שם" required><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
          <FormField label="תיאור"><TextInput value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormField>
        </div>
      </Modal>

      {deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} title="מחיקת תסריט"
          footer={<><button type="button" className="btn" onClick={() => setDeleteTarget(null)}>ביטול</button><button type="button" className="btn danger" onClick={() => remove(deleteTarget)}>מחק</button></>}>
          <p>למחוק את התסריט <strong>{deleteTarget.name}</strong>?</p>
        </Modal>
      )}

      <Drawer isOpen={!!editor} onClose={() => setEditor(null)} title={editor?.name} width={620}>
        {editor && <ScriptEditor script={editor} onSaved={() => { setEditor(null); load(); }} />}
      </Drawer>
    </div>
  );
}

interface Placeholder { key: string; he: string; sample: string }

function ScriptEditor({ script, onSaved }: { script: Script; onSaved: () => void }) {
  const [name, setName] = useState(script.name);
  const [description, setDescription] = useState(script.description ?? '');
  const [sections, setSections] = useState<Section[]>(script.tree?.sections ?? []);
  const [saving, setSaving] = useState(false);
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  // Track which section body textarea is focused so a placeholder click inserts there.
  const focused = useRef<{ index: number; pos: number } | null>(null);

  useEffect(() => {
    void adminApi.scriptPlaceholders().then(setPlaceholders).catch(() => {});
  }, []);

  function addSection() {
    setSections((s) => [...s, { title: '', body: '' }]);
  }
  function updateSection(i: number, patch: Partial<Section>) {
    setSections((s) => s.map((sec, idx) => (idx === i ? { ...sec, ...patch } : sec)));
  }
  function removeSection(i: number) {
    setSections((s) => s.filter((_, idx) => idx !== i));
  }
  function move(i: number, dir: -1 | 1) {
    setSections((s) => {
      const j = i + dir;
      if (j < 0 || j >= s.length) return s;
      const copy = [...s];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  }

  // Insert {{key}} into the last-focused body at the caret (or append).
  function insertPlaceholder(key: string) {
    const token = `{{${key}}}`;
    const f = focused.current;
    if (!f) {
      toast('מקם את הסמן בתוך טקסט של שלב', 'warn');
      return;
    }
    setSections((s) => s.map((sec, idx) => {
      if (idx !== f.index) return sec;
      const body = sec.body;
      const pos = Math.min(f.pos, body.length);
      return { ...sec, body: body.slice(0, pos) + token + body.slice(pos) };
    }));
  }

  // Build a sample-value map for the live preview.
  const sampleVars = Object.fromEntries(placeholders.map((p) => [p.key, p.sample]));
  function renderPreview(text: string): string {
    return text.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (m, k) => sampleVars[k.toLowerCase()] ?? m);
  }

  async function save() {
    if (!name.trim()) {
      toast('יש למלא שם', 'danger');
      return;
    }
    setSaving(true);
    try {
      await adminApi.updateScript(script.id, {
        name: name.trim(),
        description: description || null,
        tree: { sections: sections.filter((s) => s.title.trim() || s.body.trim()) },
      });
      toast('נשמר', 'ok');
      onSaved();
    } catch {
      toast('שגיאה בשמירה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      <FormField label="שם" required><TextInput value={name} onChange={(e) => setName(e.target.value)} /></FormField>
      <FormField label="תיאור"><TextInput value={description} onChange={(e) => setDescription(e.target.value)} /></FormField>

      {placeholders.length > 0 && (
        <div style={{ padding: 'var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 6 }}>
            משתנים — לחץ כדי להוסיף לטקסט השלב שבעריכה. יוחלפו אוטומטית בנתוני הלקוח בזמן השיחה.
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {placeholders.map((p) => (
              <button
                key={p.key}
                type="button"
                className="btn ghost"
                style={{ padding: '4px 8px', fontSize: 'var(--t-xs)' }}
                onMouseDown={(e) => e.preventDefault() /* keep textarea focus */}
                onClick={() => insertPlaceholder(p.key)}
                title={p.he}
              >
                {p.he} <code dir="ltr" style={{ opacity: 0.6 }}>{`{{${p.key}}}`}</code>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="stack" style={{ gap: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: 'var(--t-sm)' }}>שלבי התסריט ({sections.length})</strong>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="btn ghost" onClick={() => setShowPreview((v) => !v)}>
              {showPreview ? 'עריכה' : 'תצוגה מקדימה'}
            </button>
            <button type="button" className="btn" onClick={addSection}>+ הוסף שלב</button>
          </div>
        </div>
        {sections.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין שלבים. הוסף שלב כדי שהנציג ידע מה לומר.</div>
        ) : showPreview ? (
          sections.map((sec, i) => (
            <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 'var(--pad-card)' }}>
              <strong style={{ fontSize: 'var(--t-sm)' }}>{i + 1}. {renderPreview(sec.title)}</strong>
              <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', marginTop: 4, whiteSpace: 'pre-wrap' }}>{renderPreview(sec.body)}</div>
            </div>
          ))
        ) : (
          sections.map((sec, i) => (
            <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 'var(--pad-card)' }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', minWidth: 20 }}>{i + 1}.</span>
                <TextInput value={sec.title} onChange={(e) => updateSection(i, { title: e.target.value })} placeholder="כותרת השלב" />
                <button type="button" className="btn ghost" onClick={() => move(i, -1)} disabled={i === 0} aria-label="למעלה">↑</button>
                <button type="button" className="btn ghost" onClick={() => move(i, 1)} disabled={i === sections.length - 1} aria-label="למטה">↓</button>
                <button type="button" className="btn danger" onClick={() => removeSection(i)} aria-label="מחק">✕</button>
              </div>
              <textarea
                value={sec.body}
                onChange={(e) => updateSection(i, { body: e.target.value })}
                onFocus={(e) => { focused.current = { index: i, pos: e.target.selectionStart ?? sec.body.length }; }}
                onSelect={(e) => { focused.current = { index: i, pos: (e.target as HTMLTextAreaElement).selectionStart ?? sec.body.length }; }}
                rows={3}
                placeholder="הטקסט שהנציג אומר בשלב זה… אפשר להשתמש ב-{{customer_name}}"
                style={{ width: '100%', padding: 10, borderRadius: 'var(--r-2)', border: '1px solid var(--line)', background: 'var(--surface)', resize: 'vertical' }}
              />
            </div>
          ))
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <StatusBadge tone={script.active ? 'ok' : 'neutral'} dot size="sm">{script.active ? 'פעיל' : 'כבוי'}</StatusBadge>
        <button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמור תסריט'}</button>
      </div>
    </div>
  );
}
